import React, { useCallback, useEffect, useRef, useState } from "react";

import { Download, File, FileText, Plus, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { ListSkeleton } from "~/components/ui/skeletons";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import DocumentUploadForm from "~/pages/purchase-orders/components/sales-request/DocumentUploadForm";
import { useSalesRequestDocuments } from "~/pages/purchase-orders/hooks/useSalesRequestDocuments";
import { useProfileStore } from "~/store/profileStore";
import { DocumentType, SalesRequestDocument } from "~/types/client";
import { showConfirmDialog } from "~/utils/swalHelpers";

interface DocumentsSectionProps {
  salesRequestId: string;
  isEditable?: boolean;
  orderStatus?: string;
}

const documentTypeLabels: Record<DocumentType, string> = {
  orden_de_compra: "Orden de Compra",
  certificado_de_calidad: "Certificado de Calidad",
  ficha_tecnica: "Ficha Técnica",
  factura: "Factura",
  nota_de_credito: "Nota de Crédito",
  nota_de_debito: "Nota de Débito",
  guia_de_despacho_electronica: "Guía de Despacho Electrónica",
  proof_of_delivery: "Comprobante de Entrega",
  hes: "HES (Hoja de Entrada de Servicios)",
};

// Get a nice display for file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " bytes";
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  else return (bytes / 1048576).toFixed(1) + " MB";
};

export const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  salesRequestId,
  isEditable = false,
  orderStatus,
}) => {
  // Local component state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const uploadInProgressRef = useRef(false);

  // Get user role to check permissions
  const profileStore = useProfileStore();
  const isTransportista = profileStore.role === "transportista";
  const isLogistica = profileStore.role === "logistica";
  const isSeller = profileStore.role === "seller";

  // External hook for documents data
  const { documents, loading, deleteDocument, refresh } =
    useSalesRequestDocuments(salesRequestId);

  // User info
  const { id: userId, role: userRole } = useProfileStore();

  // Effect to prevent closing form while upload is in progress
  useEffect(() => {
    // If we're not showing the form anymore, reset uploading state
    if (!showUploadForm) {
      setIsUploading(false);
      uploadInProgressRef.current = false;
    }
  }, [showUploadForm]);

  // Check if the user can edit documents
  const canEdit =
    isEditable &&
    (userRole === "superadmin" ||
      userRole === "admin" ||
      documents.some((doc) => doc.createdBy === userId));

  // Everyone should be able to create documents, but only document owners, admins, and superadmins can delete
  // Exclude transportista and logistica users from creating documents
  const canCreateDocuments = isEditable && !isTransportista && !isLogistica;
  // BSale-backed facturas / credit notes are sensitive: deleting only removes the
  // local record (never BSale/SII), so restrict them to superadmin behind a warning.
  const isBsaleTaxDoc = (doc: SalesRequestDocument) =>
    (doc.documentType === "factura" ||
      doc.documentType === "nota_de_credito") &&
    !!doc.bsaleNumber;

  const canDeleteDocument = (doc: SalesRequestDocument) => {
    if (!isEditable) return false;
    if (isBsaleTaxDoc(doc)) return userRole === "superadmin";
    return (
      userRole === "superadmin" ||
      userRole === "admin" ||
      doc.createdBy === userId
    );
  };

  // Get document icon based on file type
  const getDocumentIcon = (doc: SalesRequestDocument) => {
    // Handle cases where fileType might be null or undefined
    if (!doc.fileType) {
      return <File className="h-8 w-8 text-blue-500" />;
    }

    if (doc.fileType.startsWith("image/")) {
      return (
        <img
          src={doc.fileUrl}
          alt="Preview"
          className="h-8 w-8 rounded object-cover"
        />
      );
    }
    if (doc.fileType.includes("pdf"))
      return <FileText className="h-8 w-8 text-red-500" />;
    return <File className="h-8 w-8 text-blue-500" />;
  };

  // Open document in new window
  const viewDocument = (url: string) => {
    window.open(url, "_blank");
  };

  // Handle document deletion. BSale facturas/NCs are removed ATOMICALLY: we void
  // them in BSale first (via the delete-bsale-document edge function) and only
  // delete the local record if BSale actually removed it — so Aliace and BSale
  // never drift apart. A real electronic factura can't be deleted in BSale, so
  // nothing is removed; it must be reversed with a credit note.
  const handleDeleteDocument = async (doc: SalesRequestDocument) => {
    const docLabel =
      doc.documentType === "nota_de_credito" ? "nota de crédito" : "factura";

    if (isBsaleTaxDoc(doc)) {
      const confirmed = await showConfirmDialog({
        title: `¿Eliminar ${docLabel}?`,
        html: `
          <div class="text-left text-sm">
            <p>Se intentará eliminar <strong>${doc.fileName}</strong>${
              doc.bsaleNumber ? ` (folio ${doc.bsaleNumber})` : ""
            } de <strong>BSale</strong> y de Aliace.</p>
            <p class="mt-2">Solo es posible con documentos de <strong>prueba / no electrónicos</strong>.</p>
            <p class="mt-1 font-medium text-red-600">Una ${docLabel} electrónica real NO se puede eliminar: anúlala con una Nota de Crédito.</p>
          </div>
        `,
        icon: "warning",
        confirmButtonText: "Sí, eliminar",
        confirmButtonColor: "#dc2626",
      });
      if (!confirmed) return;

      // Atomic: void in BSale first; only remove locally if BSale deleted it.
      const { data, error } = await supabase.functions.invoke(
        "delete-bsale-document",
        { body: { documentId: doc.id } }
      );

      if (error || !data) {
        toast({
          title: "Error",
          description:
            "No se pudo eliminar en BSale, por lo que tampoco se eliminó de Aliace.",
          variant: "destructive",
        });
        return;
      }

      if (!data.deletedInBsale) {
        toast({
          title: "No se eliminó",
          description:
            data.message ||
            "El documento no se pudo eliminar en BSale, por lo que se mantiene en Aliace.",
          variant: "destructive",
        });
        return;
      }

      const success = await deleteDocument(doc.id);
      if (success) {
        refresh();
        toast({
          title: "Documento eliminado",
          description: "Se eliminó de BSale y de Aliace.",
        });
      }
      return;
    }

    // Non-BSale documents (uploads, guías, etc.): plain local delete.
    const confirmed = await showConfirmDialog({
      title: "¿Eliminar documento?",
      text: `¿Estás seguro de eliminar "${doc.fileName}"? Esta acción no se puede deshacer.`,
      icon: "warning",
      confirmButtonText: "Sí, eliminar",
      confirmButtonColor: "#dc2626",
    });
    if (!confirmed) return;

    const success = await deleteDocument(doc.id);
    if (success) {
      refresh();
    }
  };

  // Signal upload start
  const handleUploadStart = useCallback(() => {
    console.log("Upload started");
    setIsUploading(true);
    uploadInProgressRef.current = true;
  }, []);

  // Handle document upload completion
  const handleUploadComplete = useCallback(
    (success: boolean, updatedDocs?: SalesRequestDocument[]) => {
      console.log(`Upload completed with success=${success}`);

      if (success) {
        toast({
          title: "Documento subido",
          description: "El documento ha sido subido correctamente.",
        });

        // Refresh document list
        refresh();

        // Schedule form closing after a delay
        setTimeout(() => {
          setIsUploading(false);
          uploadInProgressRef.current = false;

          // Only close the form after delay if still showing
          if (showUploadForm) {
            setShowUploadForm(false);
          }
        }, 1500); // Longer delay to ensure refresh completes
      } else {
        setIsUploading(false);
        uploadInProgressRef.current = false;
      }
    },
    [refresh, showUploadForm]
  );

  // Toggle document upload form display
  const toggleUploadForm = useCallback(() => {
    // Prevent toggling if upload is in progress
    if (uploadInProgressRef.current) {
      console.log("Upload in progress, ignoring toggle request");
      return;
    }

    setShowUploadForm((prev) => !prev);
  }, []);

  // Group documents by type
  const documentsByType: Record<DocumentType, SalesRequestDocument[]> = {
    orden_de_compra: [],
    certificado_de_calidad: [],
    ficha_tecnica: [],
    factura: [],
    nota_de_credito: [],
    nota_de_debito: [],
    guia_de_despacho_electronica: [],
    proof_of_delivery: [],
    hes: [],
  };

  documents.forEach((doc) => {
    if (doc.type in documentsByType) {
      documentsByType[doc.type as DocumentType].push(doc);
    }
  });

  return (
    <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
        <h3 className="flex items-center text-lg font-semibold text-gray-800">
          <FileText className="mr-2 h-5 w-5 text-blue-600" />
          Documentos
        </h3>
        {!isTransportista &&
          !isLogistica &&
          // For seller users, hide button when status is "in_transit" or "delivered"
          (!isSeller ||
            (isSeller &&
              orderStatus !== "in_transit" &&
              orderStatus !== "delivered")) && (
            <Button
              variant="outline"
              size="sm"
              onClick={toggleUploadForm}
              disabled={isUploading || !canCreateDocuments}
            >
              {showUploadForm ? (
                isUploading ? (
                  <span className="flex items-center">
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></span>
                    Subiendo...
                  </span>
                ) : (
                  "Cancelar"
                )
              ) : (
                <>
                  <Plus className="mr-1 h-4 w-4" />
                  Crear documento adjunto
                </>
              )}
            </Button>
          )}
      </div>

      <div className="p-4">
        {showUploadForm && (
          <div className="mb-4 rounded-md border bg-gray-50 p-3">
            <h4 className="mb-2 text-sm font-medium">Subir Documento</h4>
            <DocumentUploadForm
              salesRequestId={salesRequestId}
              onUploadStart={handleUploadStart}
              onUploadComplete={handleUploadComplete}
            />
          </div>
        )}

        {loading && !showUploadForm ? (
          <ListSkeleton rows={2} />
        ) : Object.entries(documentsByType).some(
            ([_, docs]) => docs.length > 0
          ) ? (
          // Documents found - display by type
          Object.entries(documentsByType).map(([type, docs]) => {
            if (docs.length === 0) return null;
            return (
              <div key={type} className="mb-4">
                <h4 className="mb-2 text-sm font-medium">
                  {documentTypeLabels[type as DocumentType]}
                </h4>
                <div className="space-y-2">
                  {docs.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-2 rounded border bg-gray-50 p-3 hover:bg-gray-100"
                    >
                      {getDocumentIcon(doc)}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          {doc.fileName}
                        </div>
                        <div className="flex items-center text-xs text-gray-500">
                          <span>{formatFileSize(doc.fileSize)}</span>
                          <span className="mx-1">•</span>
                          <span>
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </span>
                          {doc.type === "orden_de_compra" &&
                            doc.orderNumber && (
                              <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">
                                OC: {doc.orderNumber}
                              </span>
                            )}
                          {doc.type === "orden_de_compra" &&
                            doc.documentDate && (
                              <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">
                                Fecha: {doc.documentDate}
                              </span>
                            )}
                          {doc.type === "guia_de_despacho_electronica" &&
                            doc.orderNumber && (
                              <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">
                                Guía: {doc.orderNumber}
                              </span>
                            )}
                          {doc.type === "guia_de_despacho_electronica" &&
                            doc.documentDate && (
                              <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">
                                Fecha: {doc.documentDate}
                              </span>
                            )}
                          {doc.type === "hes" && doc.orderNumber && (
                            <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">
                              HES: {doc.orderNumber}
                            </span>
                          )}
                          {doc.type === "hes" && doc.documentDate && (
                            <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">
                              Fecha: {doc.documentDate}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => viewDocument(doc.fileUrl)}
                          title="Ver documento"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {canDeleteDocument(doc) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-700"
                            onClick={() => handleDeleteDocument(doc)}
                            title="Eliminar documento"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          // No documents found - show empty state
          <div className="py-8 text-center text-gray-500">
            <FileText className="mx-auto mb-2 h-12 w-12 text-gray-300" />
            <p>No hay documentos adjuntos</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsSection;
